import { useEffect, useMemo, useState } from 'react';
import { Layer } from 'shared/map-exports';
import { MapLayerProps } from 'features/mapping/mapping.types';
import { addVisible } from 'shared/addVisible';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import { getWatchdutyTileUrl } from '../layers.utils';
import {
  SOURCE_LAYER,
  SOURCE_ID,
  REFRESH_INTERVAL_SECONDS,
  CIRCLE_LAYER_ID,
} from './HeatDetectionsLayer.constants';
import { getCircleStyle } from './HeatDetectionsLayer.utils';

type HeatDetectionsLayerProps = MapLayerProps & {
  enabledSatellites: string[];
};

export const HeatDetectionsLayer = (
  props: HeatDetectionsLayerProps,
): JSX.Element => {
  const { visible, enabledSatellites } = props;

  // Periodically regenerate the style so the injected `nowEpochSeconds`
  // stays current, keeping the 24-hour filter and color gradient accurate.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!visible) return undefined;
    // Refresh immediately on becoming visible: while hidden the interval is
    // cleared, so the last-computed style (and its captured `nowEpochSeconds`)
    // could otherwise be stale by the hidden duration when re-shown.
    setTick((t) => t + 1);
    const id = setInterval(
      () => setTick((t) => t + 1),
      REFRESH_INTERVAL_SECONDS * 1000,
    );
    return () => clearInterval(id);
  }, [visible]);

  const circleStyle = useMemo(
    () => getCircleStyle(enabledSatellites),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabledSatellites, tick],
  );

  return (
    <VisibilityToggledSource
      id={SOURCE_ID}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      visible={visible}
    >
      <Layer
        id={CIRCLE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(circleStyle, visible)}
      />
    </VisibilityToggledSource>
  );
};
